import React from 'react';
import { Dropdown, Input, MultiSelectDropdown } from 'freemium-ui';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { blockInvalidInteger } from 'routes/utils';
import QuillEditor from 'shared/components/QuillEditor';
import { useGetSurvey, useGetTransactionFields } from 'shared/hooks';
import { WorkflowActionDelayTypes, WorkflowType } from 'shared/types';
import { WorkFlowDelayTypeOptionType } from '../../types';
import { useGetWorkflowPlaceholders } from '../../hooks';
import { getWorkflowPlaceholders } from '../../utils';
import { ActionContainer } from '../../styles';
import { WorkFlowDelayTypeOptions } from '../../constants';
import EmailVerifyInput from './email-verify-input';

type SendAnEmailProps = {
  toRespondent?: boolean;
  disabled: boolean;
  actionIndex: number;
};

const SendAnEmail = (props: SendAnEmailProps) => {
  const { disabled, actionIndex, toRespondent } = props;
  const { t } = useTranslation();
  const {
    watch,
    register,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<WorkflowType>();

  const event = watch('event');
  const survey_id = watch('survey_id');
  const subject = watch(`actions.${actionIndex}.action_meta.subject`);
  const body = watch(`actions.${actionIndex}.action_meta.body`);
  const to_mail = watch(`actions.${actionIndex}.action_meta.to_mail`);
  const delay_type = watch(`actions.${actionIndex}.action_meta.delay_type`);
  const from_mail = watch(`actions.${actionIndex}.action_meta.from_mail`);

  const { data: allPlaceholders } = useGetWorkflowPlaceholders();

  const {
    data: surveyData,
    isFetching: isFetchingSurvey,
    // isError: isSurveyError,
  } = useGetSurvey({
    surveyId: survey_id || '',
    enabled: !!survey_id,
  });

  const { data: transactionFields, isFetching: isFetchingTransactionFields } =
    useGetTransactionFields({
      surveyId: survey_id || '',
      enabled: !!survey_id,
    });

  const placeholders = React.useMemo(
    () =>
      getWorkflowPlaceholders({
        surveyData,
        transactionFields,
        allPlaceholders: {
          ...allPlaceholders,
        },
        event,
      }),
    [surveyData, allPlaceholders, event],
  );

  const workFlowDelayTypeOptions = React.useMemo<WorkFlowDelayTypeOptionType[]>(
    () =>
      Object.keys(WorkFlowDelayTypeOptions).map((key) => ({
        id: key as WorkflowActionDelayTypes,
        label: t(WorkFlowDelayTypeOptions[key]),
      })),
    [t],
  );

  return (
    <ActionContainer>
      <div className="t-w-h-row">
        <div className="t-w-h-flex">
          <Input
            {...register(`actions.${actionIndex}.action_meta.from_name`)}
            id="from_name"
            disabled={disabled}
            label={`${t('forms.labels.sender_name')}`}
            placeholder={`${t('forms.placeholders.email_name')}`}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `actions.${actionIndex}.action_meta.from_name.message`,
              )
            }
            errorText={get(
              errors,
              `actions.${actionIndex}.action_meta.from_name.message`,
              '',
            )}
          />
        </div>
        <div className="t-w-h-flex">
          <EmailVerifyInput
            value={from_mail || ''}
            onChange={(emailString: string) =>
              setValue(
                `actions.${actionIndex}.action_meta.from_mail`,
                emailString,
                { shouldValidate: true },
              )
            }
            isRequired
            label={`${t('workflow.action.from_email')}`}
            placeholder={`${t('forms.placeholders.from_email_address')}`}
            disabled={disabled}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `actions.${actionIndex}.action_meta.from_mail.message`,
              )
            }
            errorText={get(
              errors,
              `actions.${actionIndex}.action_meta.from_mail.message`,
              '',
            )}
          />
        </div>
      </div>
      <div className="t-w-h-row">
        <div className="t-w-h-flex">
          <EmailVerifyInput
            value={from_mail || ''}
            onChange={(emailString: string) =>
              setValue(
                `actions.${actionIndex}.action_meta.reply_to`,
                emailString,
                { shouldValidate: true },
              )
            }
            label={`${t('forms.labels.reply_to')}`}
            placeholder={`${t('forms.placeholders.reply_to')}`}
            disabled={disabled}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `actions.${actionIndex}.action_meta.reply_to.message`,
              )
            }
            errorText={get(
              errors,
              `actions.${actionIndex}.action_meta.reply_to.message`,
              '',
            )}
          />
        </div>
        {!toRespondent && (
          <div className="t-w-h-flex">
            <MultiSelectDropdown
              itemLabelIdentifier="label"
              itemValueIdentifier="id"
              items={[]}
              name="to_mail"
              isRequired
              onChange={(
                vals: { customValues: boolean; id: string; label: string }[],
              ) => {
                setValue(
                  `actions.${actionIndex}.action_meta.to_mail`,
                  vals.map((v) => v.label),
                  { shouldValidate: true },
                );
              }}
              customValues
              selectedItem={(to_mail || []).map((item) => ({
                id: item,
                label: item,
              }))}
              id="to_email_address"
              disabled={disabled}
              label={`${t('forms.labels.recipient_email')}`}
              placeholder={`${t('forms.placeholders.to_email_address')}`}
              isErrored={
                submitCount > 0 &&
                (get(errors, `actions.${actionIndex}.action_meta.to_mail`, [])
                  .length > 0 ||
                  !!get(
                    errors,
                    `actions.${actionIndex}.action_meta.to_mail.message`,
                  ))
              }
              errorText={
                get(errors, `actions.${actionIndex}.action_meta.to_mail`, [])
                  .length > 0
                  ? get(
                      errors,
                      `actions.${actionIndex}.action_meta.to_mail.0.message`,
                      '',
                    )
                  : get(
                      errors,
                      `actions.${actionIndex}.action_meta.to_mail.message`,
                      '',
                    )
              }
            />
          </div>
        )}
      </div>
      <div className="t-w-h-row">
        <QuillEditor
          id="quill-wf-subject"
          getValueAs="HTML"
          placeholders={placeholders}
          placeholderConfig={{ isAddSpaceAtEnd: true }}
          label={`${t('forms.labels.subject')}`}
          placeholder={`${t('forms.placeholders.subject')}`}
          disabled={isFetchingSurvey || disabled}
          value={subject || ''}
          isRequired
          maxCharLimit={230}
          onChange={(val: string) =>
            setValue(`actions.${actionIndex}.action_meta.subject`, val, {
              shouldValidate: true,
            })
          }
          isErrored={
            submitCount > 0 &&
            !!get(errors, `actions.${actionIndex}.action_meta.subject.message`)
          }
          errorText={get(
            errors,
            `actions.${actionIndex}.action_meta.subject.message`,
            '',
          )}
        />
      </div>
      <div className="t-w-h-row">
        <QuillEditor
          id="quill-wf-body"
          getValueAs="HTML"
          placeholders={placeholders}
          placeholderConfig={{ isAddSpaceAtEnd: true }}
          showActions
          isRequired
          label={`${t('forms.labels.body')}`}
          placeholder={`${t('forms.placeholders.body')}`}
          disabled={isFetchingSurvey || disabled}
          value={body || ''}
          maxCharLimit={5000}
          onChange={(val: string) =>
            setValue(`actions.${actionIndex}.action_meta.body`, val, {
              shouldValidate: true,
            })
          }
          isErrored={
            submitCount > 0 &&
            !!get(errors, `actions.${actionIndex}.action_meta.body.message`)
          }
          errorText={get(
            errors,
            `actions.${actionIndex}.action_meta.body.message`,
            '',
          )}
        />
      </div>
      <div className="t-w-h-row">
        <div className="wf-sb-label" style={{ margin: 0 }}>
          {t('workflow.action.email_delay')}
        </div>
        <Input
          {...register(`actions.${actionIndex}.action_meta.delay`)}
          id="delay"
          onKeyDown={blockInvalidInteger}
          disabled={disabled}
          inline
          type="number"
          min={0}
          max={365}
          isErrored={
            submitCount > 0 &&
            !!get(errors, `actions.${actionIndex}.action_meta.delay.message`)
          }
          errorText={get(
            errors,
            `actions.${actionIndex}.action_meta.delay.message`,
            '',
          )}
        />
        <Dropdown
          defaultSelectedItem={
            workFlowDelayTypeOptions?.find((f) => f.id === delay_type) || null
          }
          width="320px"
          itemToString={(s) => s.label || ''}
          itemComponent={(s) => s.label || ''}
          items={workFlowDelayTypeOptions || []}
          disabled={disabled}
          onChange={(item: WorkFlowDelayTypeOptionType) => {
            setValue(`actions.${actionIndex}.action_meta.delay_type`, item.id);
          }}
          emptyItemsText={`${t('no_results_found')}`}
          isErrored={
            submitCount > 0 &&
            !!get(
              errors,
              `actions.${actionIndex}.action_meta.delay_type.message`,
            )
          }
          errorText={get(
            errors,
            `actions.${actionIndex}.action_meta.delay_type.message`,
            '',
          )}
        />
      </div>
    </ActionContainer>
  );
};

export default SendAnEmail;
